import { useRef } from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { notificationPreferencesQuery } from "../queries";
import { setNotificationPreferenceCategoryMutation } from "../commands";
import type { ListNotificationPreferencesResponse, NotificationPreferenceItem } from "../../client-messages";
import {
  DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
  type NotificationCategory,
  type NotificationChannel,
  type NotificationChannelPreferences,
  type NotificationPreferenceScope,
} from "../../preferences";

function scopesEqual(a: NotificationPreferenceScope, b: NotificationPreferenceScope): boolean {
  if (a.kind === "allCameras" && b.kind === "allCameras") return true;
  if (a.kind === "site" && b.kind === "site") return a.siteId === b.siteId;
  if (a.kind === "camera" && b.kind === "camera") {
    return a.siteId === b.siteId && a.assetId === b.assetId && a.cameraId === b.cameraId;
  }
  return false;
}

/** A stable string key for {@link scopesEqual}'s equivalence classes — used to key the per-scope+category write queue. */
function scopeCategoryKey(scope: NotificationPreferenceScope, category: NotificationCategory): string {
  return `${JSON.stringify(scope)}::${category}`;
}

function readScopeCategoryChannels(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: NotificationPreferenceScope,
  category: NotificationCategory,
): NotificationChannelPreferences {
  const list = queryClient.getQueryData<ListNotificationPreferencesResponse>(queryKey)?.preferences ?? [];
  const existing = list.find((item) => scopesEqual(item.scope, scope));
  return existing?.preferences[category] ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES[category];
}

/**
 * Writes one scope+category's channels into the cached list, leaving every
 * other entry (including ones written by a concurrent call for a different
 * scope+category) untouched — used for both the optimistic write and a
 * same-call rollback, so neither can clobber an unrelated write.
 */
function writeScopeCategoryChannels(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: NotificationPreferenceScope,
  category: NotificationCategory,
  channels: NotificationChannelPreferences,
): void {
  queryClient.setQueryData<ListNotificationPreferencesResponse>(queryKey, (old) => {
    const list = old?.preferences ?? [];
    const existing = list.find((item) => scopesEqual(item.scope, scope));
    const updated: NotificationPreferenceItem = {
      scope,
      preferences: { ...(existing?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES), [category]: channels },
    };
    return {
      preferences:
        existing ? list.map((item) => (scopesEqual(item.scope, scope) ? updated : item)) : [...list, updated],
    };
  });
}

export interface NotificationPreferencesState {
  /** Every scope the user has saved a preference for — raw, unmerged with the tenant's site/camera list. */
  readonly preferences: readonly NotificationPreferenceItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  /**
   * Flips one channel within one scope+category, replacing that category's
   * whole channel triple on the server (see `NotificationPreferencesService`) —
   * the other two channels are read fresh from the cache rather than trusted
   * from the caller, so this never needs a pre-built triple. Call once per
   * toggle, immediately, rather than batching several toggles behind a "Save"
   * button.
   *
   * The untouched channels come from this scope+category's own row alone (all
   * off if it has none), never from a broader scope. Scopes combine by OR at
   * dispatch, so a row only needs to hold what was ticked for that scope
   * itself: channels a broader scope grants still apply, and the screen shows
   * them as inherited. Copying them in would outlive the broader scope — e.g.
   * toggling SMS on for a site whose Email is on via "All Cameras" writes only
   * SMS for that site, so turning "All Cameras" Email off turns it off there
   * too.
   *
   * Writes for the same scope+category are serialized (a second call waits for
   * the first to finish applying before it reads "current") so two quick
   * toggles on the same row compose instead of the second one clobbering the
   * first with a stale snapshot. A failure only rolls back its own write — the
   * specific scope+category's previous channels, not a whole-list snapshot —
   * so it can't undo an unrelated write that landed in between.
   */
  readonly setCategory: (
    scope: NotificationPreferenceScope,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => Promise<void>;
}

/**
 * Raw API wiring for self-service notification preferences — deliberately has
 * no knowledge of the tenant's actual sites/cameras (names, which ones exist).
 * A caller merges `preferences` against its own site/camera list to render a
 * complete settings screen (a scope with no saved row here just means "not
 * configured yet", not "doesn't exist") — kept out of this feature since that
 * list comes from provisioning, not alarm-schedules (ADR-0026).
 */
export function useNotificationPreferences(): NotificationPreferencesState {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(notificationPreferencesQuery(apiConfig, {}));

  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  const setCategoryMutation = useMutation(setNotificationPreferenceCategoryMutation(mutationConfig, {}));

  // Chains writes per scope+category key so a second toggle on the same row
  // only starts once the first has applied its optimistic write — see the
  // `setCategory` doc comment.
  const pendingWrites = useRef(new Map<string, Promise<void>>()).current;

  const applyChannelChange = async (
    scope: NotificationPreferenceScope,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ): Promise<void> => {
    const queryOptions = notificationPreferencesQuery(apiConfig, {});
    // What's actually stored for this exact scope+category before this write —
    // possibly nothing. The untouched channels are carried forward from it
    // (see the `setCategory` doc comment), and a failed write rolls back to it.
    const previousOwnChannels = readScopeCategoryChannels(queryClient, queryOptions.queryKey, scope, category);
    const channels: NotificationChannelPreferences = { ...previousOwnChannels, [channel]: enabled };

    // Applied eagerly so the checkbox reflects the change immediately, and left
    // in place (not refetched) on success — it already exactly matches what the
    // server just accepted, and this query has no other consumer to reconcile
    // for. Refetching here would race a *different* scope+category's own
    // still-in-flight optimistic write (writes are only serialized within the
    // same scope+category — see `setCategory`), snapping its checkbox back
    // until that other request finishes. Rolled back below if this write fails.
    writeScopeCategoryChannels(queryClient, queryOptions.queryKey, scope, category, channels);

    try {
      await setCategoryMutation.mutateAsync({ scope, category, channels });
    } catch (error) {
      writeScopeCategoryChannels(queryClient, queryOptions.queryKey, scope, category, previousOwnChannels);
      throw error;
    }
  };

  const setCategory = (
    scope: NotificationPreferenceScope,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ): Promise<void> => {
    const key = scopeCategoryKey(scope, category);
    const queued = (pendingWrites.get(key) ?? Promise.resolve()).then(() =>
      applyChannelChange(scope, category, channel, enabled),
    );
    // The map only ever needs to remember that something is pending, not its
    // outcome — swallow here so a failed write doesn't wedge the queue for
    // this key; the caller below still observes the rejection via `queued`.
    pendingWrites.set(
      key,
      queued.then(
        () => undefined,
        () => undefined,
      ),
    );
    return queued;
  };

  return {
    preferences: data?.preferences ?? [],
    isLoading,
    isError,
    setCategory,
  };
}
